/**
 * Danger zone — patient-facing self-service controls:
 *   - GDPR Article 20 — download/email data export
 *   - Iter 96 account lifecycle (activate / deactivate)
 *   - GDPR Article 17 — schedule account deletion with 30-day grace
 *
 * Iter 129 (task a) — Replaced the crude `window.confirm` + `window.prompt`
 * browser dialogs with a proper branded shadcn AlertDialog. The new modal:
 *   - shows a big red banner + shield icon
 *   - lets the user pick from 5 canonical reasons (dropdown) or free-form
 *   - surfaces the exact number of medical records at risk
 *   - requires the user to type "DELETE" to confirm — impossible-to-fat-finger
 *   - has a distinct "Cancel" that's the emphasised action (guardrail)
 *
 * Iter 129 (task b) — Backend now emails the user when they schedule (or
 * cancel) a deletion, with a one-click "undo" link. No frontend change is
 * needed for that — but the "check your inbox" confirmation toast is new.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Download, Mail, KeyRound, ShieldAlert, X, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { safeConfirm } from '../../../lib/confirm';
import { t } from '../../../lib/i18n';
import { TransparencyPref } from './TransparencyPref';
import { ConsentPrefs } from './ConsentPrefs';
import { TransferOwnershipWizard } from '../../../components/TransferOwnershipWizard';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../../components/ui/alert-dialog';

const DELETION_REASONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'switching_providers', label: 'I found a different platform' },
  { value: 'privacy_concerns', label: 'Privacy or data-handling concerns' },
  { value: 'closing_clinic', label: "I'm closing my clinic / retiring" },
  { value: 'too_expensive', label: 'Cost / pricing' },
  { value: 'not_using', label: "I'm not using Physiogenic enough" },
  { value: 'other', label: 'Other' },
];

export function DangerZone({ locale = 'en' }) {
  const [status, setStatus] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Iter 129 — Delete-account confirmation modal state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reasonKey, setReasonKey] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [confirmText, setConfirmText] = useState('');

  // Iter 130 — Transfer-ownership wizard state.
  const [transferOpen, setTransferOpen] = useState(false);
  useEffect(() => {
    // Auto-open when the URL is /admin/security#transfer-ownership (from the
    // structured API error payload).
    if (window.location.hash === '#transfer-ownership') {
      setTransferOpen(true);
    }
  }, []);

  useEffect(() => {
    api.get('/auth/me/deletion-status').then(r => setStatus(r.data)).catch(() => setStatus({ scheduled: false }));
    api.get('/auth/me/account-status').then(r => setLifecycle(r.data)).catch(() => setLifecycle(null));
  }, []);

  const downloadMyData = async () => {
    setExporting(true);
    try {
      const res = await api.post('/auth/me/export-data', null, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `physiogenic-data-export-${new Date().toISOString().slice(0,10)}.json`;
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Data export downloaded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setExporting(false); }
  };

  const emailMyData = async () => {
    setExporting(true);
    try {
      const { data } = await api.post('/auth/me/email-data-export');
      toast.success(`Sent to ${data.to} (${data.total_rows} rows). Check your inbox in a minute.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setExporting(false); }
  };

  const openDeleteModal = () => {
    // Reset all form state on open so re-opens are clean.
    setReasonKey('');
    setReasonText('');
    setConfirmText('');
    setDeleteOpen(true);
  };

  const scheduleDelete = async () => {
    // Iter 129 — reason format: "<canonical_key>: <free_form_text>" or just one.
    const reason = [
      reasonKey && DELETION_REASONS.find(r => r.value === reasonKey)?.label,
      reasonText.trim(),
    ].filter(Boolean).join(' — ') || null;
    setBusy(true);
    try {
      const { data } = await api.post('/auth/me/delete-account', { reason });
      toast.success(data.message, { duration: 8000 });
      // Iter 129 (task b) — nudge the user to check their inbox.
      toast('An email with a one-click "cancel deletion" link is on its way.', {
        icon: '📮',
        duration: 6000,
      });
      const r = await api.get('/auth/me/deletion-status');
      setStatus(r.data);
      setDeleteOpen(false);
    } catch (e) {
      // Iter 130 — Backend now returns a structured payload when the caller
      // is a clinic owner. Auto-open the transfer wizard so it's a 1-tap fix.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'clinic_owner_must_transfer_first') {
        setDeleteOpen(false);
        setTransferOpen(true);
        toast.message('Transfer clinic ownership first — opening the wizard for you.');
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || e.message);
      }
    } finally { setBusy(false); }
  };

  const undelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/me/undelete-account');
      toast.success(data.message || 'Your account is safe.');
      const r = await api.get('/auth/me/deletion-status');
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const deactivate = async () => {
    if (!(await safeConfirm("Pause your account? You won't be able to log in until you reactivate. Your data stays intact."))) return;
    setBusy(true);
    try {
      await api.post('/auth/me/deactivate-account');
      toast.success('Account deactivated. Signing you out…');
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post('/auth/me/reactivate-account');
      const r = await api.get('/auth/me/account-status');
      setLifecycle(r.data);
      const r2 = await api.get('/auth/me/deletion-status');
      setStatus(r2.data);
      toast.success('Account reactivated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!status) return null;

  const hasPatientData = lifecycle?.has_patient_data && (lifecycle?.patient_data_count > 0);
  const canConfirmDelete = confirmText.trim().toUpperCase() === 'DELETE' && !busy;

  return (
    <>
      {/* GDPR Article 20 — data portability */}
      <div className="surface border border-soft rounded-lg p-4 mt-6 max-w-xl" data-testid="data-export-zone">
        <h3 className="font-heading font-semibold flex items-center gap-2 mb-1">
          <Download className="w-4 h-4 text-brand"/> {t(locale, 'your_data')}
        </h3>
        <p className="text-sm text-fg-muted mb-3">{t(locale, 'your_data_lede')}</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadMyData} disabled={exporting}
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="export-my-data-btn">
            <Download className="w-4 h-4"/> {exporting ? 'Preparing…' : t(locale, 'download_my_data')}
          </button>
          <button onClick={emailMyData} disabled={exporting}
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="email-my-data-btn">
            <Mail className="w-4 h-4"/> {t(locale, 'email_me_the_export')}
          </button>
        </div>
      </div>

      <TransparencyPref locale={locale}/>
      <ConsentPrefs locale={locale}/>

      {/* Iter 130 — Transfer clinic ownership card (owners only). We show
          this as its own dedicated panel so owners never wonder how to
          delete their account when the "Transfer first" guardrail fires. */}
      {lifecycle?.is_owner && (
        <div
          id="transfer-ownership"
          className="surface border border-soft rounded-lg p-4 mt-6 max-w-xl"
          data-testid="ownership-zone"
        >
          <h3 className="font-heading font-semibold flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-brand"/> Clinic ownership
          </h3>
          <p className="text-sm text-fg-muted mb-3">
            You&apos;re the current owner of this clinic — you handle billing,
            tier and admin lifecycle. To delete or deactivate your own account,
            transfer ownership to another admin first (or invite a new one).
          </p>
          <button
            type="button"
            onClick={() => setTransferOpen(true)}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="transfer-ownership-btn"
          >
            <ShieldCheck className="w-4 h-4"/> Transfer ownership
          </button>
        </div>
      )}

      {/* Account lifecycle */}
      <div className="surface border border-soft rounded-lg p-4 mt-6 max-w-xl" data-testid="account-lifecycle-zone">
        <h3 className="font-heading font-semibold flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-brand"/> Account status
        </h3>
        {lifecycle?.is_active === false ? (
          <div className="space-y-3" data-testid="account-deactivated">
            <p className="text-sm">Your account is currently <strong className="text-[#C62828]">deactivated</strong>. Reactivate to restore access.</p>
            <button onClick={reactivate} disabled={busy}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="reactivate-btn">
              <Check className="w-4 h-4"/> Reactivate my account
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-fg-muted">Your account is active. Need a break? Pause it without losing your data.</p>
            <button onClick={deactivate} disabled={busy || lifecycle?.is_owner}
                    className="btn-secondary inline-flex items-center gap-2"
                    title={lifecycle?.is_owner ? 'Owners must transfer ownership first' : ''}
                    data-testid="deactivate-btn">
              <AlertTriangle className="w-4 h-4"/> Deactivate (pause) my account
            </button>
            {lifecycle?.is_owner && (
              <p className="text-[10px] text-fg-muted">
                You&apos;re the clinic owner — transfer ownership before deactivating.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Danger zone */}
      <div className="bg-bg-secondary border-2 border-red-300 rounded-lg p-4 mt-6 max-w-xl" data-testid="danger-zone">
        <h3 className="font-heading font-semibold text-red-800 flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4"/> {t(locale, 'danger_zone')}
        </h3>
        {status.scheduled ? (
          <div className="space-y-3">
            <div className="text-sm" data-testid="deletion-pending">
              <p>{t(locale, 'deletion_pending_lede', { days: status.days_remaining, date: status.scheduled_for?.slice(0,10) })}</p>
            </div>
            <button onClick={undelete} disabled={busy}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="undelete-btn">
              <Check className="w-4 h-4"/> {t(locale, 'cancel_deletion')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-fg-muted">{t(locale, 'delete_account_lede')}</p>
            {hasPatientData && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800" data-testid="delete-patient-data-warning">
                <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5"/>
                You have <strong>{lifecycle.patient_data_count}</strong> medical record(s) on file. Deletion is permanent — download your data first.
              </div>
            )}
            <button onClick={openDeleteModal} disabled={busy}
                    className="px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="delete-account-btn">
              <AlertTriangle className="w-4 h-4"/> {t(locale, 'delete_my_account')}
            </button>
          </div>
        )}
      </div>

      {/* Iter 129 — Branded delete-account confirmation modal. */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="max-w-lg" data-testid="delete-account-modal">
          <button onClick={() => setDeleteOpen(false)}
                  className="absolute top-3 right-3 text-fg-muted hover:text-fg"
                  aria-label="Close"
                  data-testid="delete-account-modal-close">
            <X className="w-5 h-5"/>
          </button>
          <AlertDialogHeader>
            {/* Red hero banner */}
            <div className="flex items-center gap-3 -mx-6 -mt-6 mb-2 px-6 py-4"
                 style={{ background: 'linear-gradient(90deg, #FEE2E2 0%, #FEF3F2 100%)', borderBottom: '1px solid #FCA5A5' }}>
              <div className="w-11 h-11 rounded-full bg-red-600/10 text-red-700 grid place-items-center shrink-0">
                <ShieldAlert className="w-6 h-6"/>
              </div>
              <div>
                <AlertDialogTitle className="text-lg font-heading font-bold text-red-900">Delete your Physiogenic account?</AlertDialogTitle>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-red-700 mt-0.5">
                  30-day grace period · you can undo any time
                </div>
              </div>
            </div>

            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p className="text-fg-muted">
                  We&apos;ll immediately sign you out and mark your account for deletion.
                  You have <strong className="text-red-700">30 days</strong> to change your
                  mind — just sign back in and click <em>Cancel deletion</em>. After that,
                  everything is <strong>permanently erased</strong> and cannot be recovered.
                </p>

                {hasPatientData && (
                  <div className="rounded-md border border-red-300 bg-red-50/50 p-3">
                    <div className="text-xs font-semibold text-red-800 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5"/>
                      You have {lifecycle.patient_data_count} medical record(s) on file.
                    </div>
                    <div className="text-[11px] text-red-700 mt-1">
                      Download a copy first — regulator-grade JSON export, one click.
                    </div>
                    <button
                      type="button"
                      onClick={downloadMyData}
                      disabled={exporting}
                      className="mt-2 text-xs font-semibold text-red-800 underline underline-offset-2 hover:text-red-900 disabled:opacity-60"
                      data-testid="delete-modal-export-btn"
                    >
                      {exporting ? 'Preparing…' : 'Download my data now →'}
                    </button>
                  </div>
                )}

                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted mb-1 block">
                    Reason (optional, helps us improve)
                  </label>
                  <select
                    className="input w-full text-sm"
                    value={reasonKey}
                    onChange={e => setReasonKey(e.target.value)}
                    data-testid="delete-modal-reason-select"
                  >
                    {DELETION_REASONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <textarea
                    className="input w-full text-sm mt-2 min-h-[68px]"
                    placeholder="Anything you'd like us to know? (optional)"
                    value={reasonText}
                    onChange={e => setReasonText(e.target.value)}
                    maxLength={500}
                    data-testid="delete-modal-reason-text"
                  />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted mb-1 block">
                    Type <span className="font-mono text-red-700">DELETE</span> to confirm
                  </label>
                  <input
                    type="text"
                    className="input w-full text-sm font-mono"
                    value={confirmText}
                    onChange={e => setConfirmText(e.target.value)}
                    placeholder="DELETE"
                    autoComplete="off"
                    data-testid="delete-modal-confirm-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="delete-modal-cancel">
              Keep my account
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={scheduleDelete}
              disabled={!canConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white disabled:opacity-40"
              data-testid="delete-modal-confirm"
            >
              <AlertTriangle className="w-4 h-4 mr-1.5"/>
              Schedule deletion in 30 days
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 130 — Transfer clinic ownership wizard. */}
      <TransferOwnershipWizard
        open={transferOpen}
        onOpenChange={setTransferOpen}
        onTransferred={async () => {
          // After a successful transfer, refresh lifecycle so is_owner=false
          // and the Transfer Ownership card disappears.
          try {
            const r = await api.get('/auth/me/account-status');
            setLifecycle(r.data);
          } catch (_) { /* ignore */ }
        }}
      />
    </>
  );
}
